import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { AssignmentSectionQuestionQuery } from '@examdojo/assignment';
import { ButtonComponent } from '@examdojo/core/button';
import { SmallLayoutComponent } from '@examdojo/layout';
import {
  ChallengeActivityQuery,
  ChallengeActivityService,
  ChallengeActivityQuestionAttemptService,
} from '@examdojo/challenge';
import { PlatformService } from '@examdojo/platform';
import { debounceTime, distinctUntilChanged, EMPTY, filter, from, map, of, switchMap, take, tap } from 'rxjs';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { IconComponent } from '@examdojo/core/icon';
import { SvgIcon } from '@examdojo/core/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { assertNonNullable } from '@examdojo/util/assert';
import { ExamTimeService } from '../exam-time';
import { SubmitConfirmationDialogService } from '../submit-confirmation/submit-confirmation-dialog.service';
import { ChallengeActivityQuestionStatusComponent } from './challenge-activity-question-status.component';
import {
  ChallengeActivityQuestionSelectorService,
  QuestionsWithAttemptStatuses,
} from './challenge-activity-question-selector.service';

@Component({
  selector: 'dojo-challenge-activity-question-selector',
  standalone: true,
  imports: [
    ButtonComponent,
    SmallLayoutComponent,
    ChallengeActivityQuestionStatusComponent,
    IconComponent,
    TranslocoPipe,
  ],
  template: `
    <div class="flex flex-col">
      <div #scrollContainer class="flex flex-1 flex-row gap-1.5 overflow-y-auto scroll-smooth p-2 md:flex-col">
        @for (question of state.questions; track question.assignmentSectionQuestion.id; let i = $index) {
          <dojo-button
            #questionButton
            [fill]="'outline'"
            (click)="setChallengeQuestion(question, i)"
            [customColor]="
              state.activeQuestionId === question.assignmentSectionQuestion.question_id ? 'sky' : undefined
            "
            [class.selected-button]="state.activeQuestionId === question.assignmentSectionQuestion.question_id"
            [class.desktop-button]="!state.isMobile"
            [size]="state.isMobile ? 'm' : 'l'"
          >
            <div class="flex w-full items-center justify-start">
              @if (shouldShowStatusComponent(question)) {
                <dojo-challenge-activity-question-status [status]="question.attemptStatus" class="mr-3" />
                <span class="text-sm font-semibold">{{ getQuestionNumber(question) }}</span>
              } @else if (shouldShowGradingResult(question)) {
                <span class="text-sm font-semibold">{{ getQuestionNumber(question) }}</span>
                <div class="aligner ml-1.5 empty:hidden md:ml-auto md:w-[40px]">
                  <span class="text-sm font-semibold" [class]="getGradingScoreClass(question)">
                    {{ question.questionGradingResult!.awardedMarks }}/{{ question.questionGradingResult!.totalMarks }}
                  </span>
                </div>
              } @else if (getQuestionStatusIcon(question)) {
                <span class="text-sm font-semibold">{{ getQuestionNumber(question) }}</span>
                <div class="aligner ml-2 empty:hidden md:ml-auto md:w-[40px]">
                  <dojo-icon [icon]="getQuestionStatusIcon(question)!" [size]="18" />
                </div>
              }
            </div>
          </dojo-button>
        }
        <small-layout>
          @if (state.shouldShowSubmitButton) {
            <dojo-button [fill]="'outline'" [size]="'m'" (click)="gradeChallengeActivity()" [customColor]="'sky'">
              <span class="text-sm">{{ 'challenge.finish_and_submit' | transloco }}</span>
            </dojo-button>
          }
        </small-layout>
      </div>
    </div>
  `,
  styles: [
    `
      .desktop-button ::ng-deep {
        .ion-button.button-outline::part(native) {
          &::before {
            border-color: transparent;
            box-shadow: none;
          }
        }
      }

      .selected-button ::ng-deep {
        .ion-button.button-outline::part(native) {
          &::before {
            background-color: theme('colors.sky.50');
            border-color: theme('colors.sky.300');
            color: theme('colors.sky.300');
          }
        }
      }

      :host {
        @apply md:max-h-[calc(100vh-76px)];
      }
    `,
  ],
  host: {
    class: 'block sticky md:static top-0 z-1 md:w-[126px]',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengeActivityQuestionSelectorComponent {
  constructor(
    private readonly assignmentSectionQuestionQuery: AssignmentSectionQuestionQuery,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly challengeActivityQuestionSelectorService: ChallengeActivityQuestionSelectorService,
    private readonly destroyRef: DestroyRef,
    private readonly platformService: PlatformService,
    private readonly examTimeService: ExamTimeService,
    private readonly submitConfirmationDialogService: SubmitConfirmationDialogService,
  ) {
    this.scrollToQuestionOnCurrentQuestionChange().pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

  readonly state = connectState({
    questions: this.challengeActivityQuestionSelectorService.questionsWithAttemptStatuses$,
    inProgressChallengeActivityId: this.challengeActivityQuery.inProgressChallengeActivityId$,
    activeQuestionId: this.assignmentSectionQuestionQuery.activeQuestionId$,
    isMobile: this.platformService.isSmall$,
    shouldShowSubmitButton: this.challengeActivityQuery.ongoingActivityStatus$.pipe(
      map((status) => status === 'IN_PROGRESS'),
    ),
  });

  setChallengeQuestion(question: QuestionsWithAttemptStatuses, _index: number) {
    if (!this.state.inProgressChallengeActivityId) {
      return;
    }

    this.challengeActivityQuestionSelectorService
      .setChallengeQuestion(question.assignmentSectionQuestion, this.state.inProgressChallengeActivityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getQuestionNumber(question: QuestionsWithAttemptStatuses): string {
    return `Q${question.assignmentSectionQuestion.position + 1}`;
  }

  getGradingScorePercentage(question: QuestionsWithAttemptStatuses): number {
    if (!question.questionGradingResult) {
      return 0;
    }
    return question.questionGradingResult.awardedMarks / question.questionGradingResult.totalMarks;
  }

  getGradingScoreClass(question: QuestionsWithAttemptStatuses): string {
    const percentage = this.getGradingScorePercentage(question);

    if (percentage === 1) {
      return 'text-green-600';
    }
    if (percentage > 0.5) {
      return 'text-amber-600';
    }
    return 'text-red-600';
  }

  getQuestionStatusIcon(question: QuestionsWithAttemptStatuses): SvgIcon | null {
    if (question.questionAttemptStatus === 'SUBMITTED') {
      return 'assets/images/challenge/waiting.svg' as SvgIcon;
    }
    if (!question.questionGradingResult && question.questionAttemptStatus) {
      return 'assets/images/challenge/error.svg' as SvgIcon;
    }
    return null;
  }

  shouldShowStatusComponent(question: QuestionsWithAttemptStatuses): boolean {
    return !question.questionAttemptStatus || question.questionAttemptStatus === 'PENDING';
  }

  shouldShowGradingResult(question: QuestionsWithAttemptStatuses): boolean {
    return !!question.questionGradingResult;
  }

  gradeChallengeActivity() {
    this.examTimeService.isTimeUp$
      .pipe(
        take(1),
        switchMap((isTimeUp) => {
          return this.gradeChallengeAfterConfirmation(!isTimeUp);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
  private gradeChallengeAfterConfirmation(showConfirmation: boolean) {
    const activityId = this.state.inProgressChallengeActivityId;
    assertNonNullable(activityId, 'activityId');

    const confirmation$ = showConfirmation ? from(this.submitConfirmationDialogService.openDialog()) : of(true);
    return confirmation$.pipe(
      switchMap((confirmed) => {
        if (!confirmed) {
          return EMPTY;
        }

        return this.challengeActivityService.gradeChallengeActivity(activityId).pipe(
          switchMap(() => {
            return this.challengeActivityQuestionAttemptService.markAllQuestionAttemptsAsDone(activityId);
          }),
        );
      }),
    );
  }

  private scrollToQuestionOnCurrentQuestionChange() {
    return this.challengeActivityQuery.ongoingActivity$.pipe(
      map((activity) => activity?.current_question_id),
      filter(isNotNullish),
      distinctUntilChanged(),
      debounceTime(0),
      tap((currentQuestionId) => {
        const questions = this.state.questions;
        if (questions && Array.isArray(questions)) {
          const activeIndex = questions.findIndex((q) => q.assignmentSectionQuestion.question_id === currentQuestionId);
          if (activeIndex !== -1) {
            this.scrollToQuestion(activeIndex);
          }
        }
      }),
    );
  }

  private scrollToQuestion(index: number) {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) {
      return;
    }

    const buttons = container.querySelectorAll('dojo-button');
    const targetButton = buttons[index] as HTMLElement;

    if (!targetButton) {
      return;
    }

    const isMobile = this.state.isMobile;

    if (isMobile) {
      const containerWidth = container.clientWidth;
      const targetLeft = targetButton.offsetLeft;
      const targetWidth = targetButton.offsetWidth;

      const scrollLeft = targetLeft - containerWidth / 2 + targetWidth / 2;
      const finalScrollLeft = Math.max(0, Math.min(scrollLeft, container.scrollWidth - containerWidth));

      container.scrollTo({
        left: finalScrollLeft,
        behavior: 'smooth',
      });
      return;
    }

    const containerHeight = container.clientHeight;
    const targetTop = targetButton.offsetTop;
    const targetHeight = targetButton.offsetHeight;

    // Calculate scroll position to center the button
    const scrollTop = targetTop - containerHeight / 2 + targetHeight / 2;
    const finalScrollTop = Math.max(0, Math.min(scrollTop, container.scrollHeight - containerHeight));

    container.scrollTo({
      top: finalScrollTop,
      behavior: 'smooth',
    });
  }
}
